import { CrxButtonIcon, CrxHeading, CrxIcon, CrxSearchContainer, CrxTable, CrxTableDataLabel, CrxTableDataRow, CrxTableHeadLabel, CrxTableHeadRow, CrxLoading } from '@cortex/cortex-core'
import { ROUTES_NAMES } from 'constants/name.routes'
import { useNavigate } from 'react-router-dom'
import { Container, FilterContainer, HeaderContainer } from './styles'
import { highlightText } from 'utils/stringUtils'
import { useUnities } from './hooks/useUnities'
import { useInView } from 'react-intersection-observer'
import { useEffect, useState, useCallback } from 'react'
import { TbArrowsUpDown } from 'react-icons/tb'

const tableHeader = [
  { 
    title: "Unidade", 
    span: 7,
    sort: true,
    field: 'name'
  },
  { title: "Setores ativos", span: 2 },
  { title: "Setores inativos", span: 2 },
  {
    title: "Ação", span: 1,
    icon: <CrxIcon
      icon="GoLine"
      size="md"
    />
  },
]

const Unities = () => {
  const { filterValue, locationSelector, setFilterValue, loadMore, isLoading, hasMoreData, locationOrder, setLocationOrder } = useUnities()
  const navigate = useNavigate()
  
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  })

  useEffect(() => {
    if (inView && hasMoreData && !isLoading) {
      loadMore()
    }
  }, [inView, hasMoreData, isLoading, loadMore])

  /**
   * Responsável por definir a ordenação por campo selecionado na coluna da Table
   * Controla também o estado do orderBy
   * @param field
   */
  const handleClickOrderBy = useCallback((field: string) => {
    const currentOrder = locationOrder.order
    const selectedOrder = currentOrder === 'ORDERBY' ? 'ORDERBYDESC' : 'ORDERBY'
    
    const orderFieldSelected = { field, order: selectedOrder }
    handlerTableOrderByField(orderFieldSelected)
  }, [locationOrder.order])

  /**
   * Usado para capturar o valor de ordenação escolhido na Table
   * @param orderFieldSelected - Valor retornado do componente Table ao clicar na ordenação
   */
  const handlerTableOrderByField = useCallback((orderFieldSelected: any) => {
    console.log('=== orderFieldSelected', orderFieldSelected)
    // Mapeamento de campos para ordenação
    const fieldMapping: { [key: string]: string } = {
      'name': 'name', // Campo da unidade
    }
    
    // Aplica o mapeamento se existir, senão usa o campo original
    const mappedField = fieldMapping[orderFieldSelected.field] || orderFieldSelected.field
    
    const mappedOrder = {
      field: mappedField,
      order: orderFieldSelected.order
    }
    
    setLocationOrder(mappedOrder)
    console.log('Mapped Order:', mappedOrder)
  }, [setLocationOrder])

  const handleNavigateBack = useCallback(() => {
    navigate(`${ROUTES_NAMES.configurationsRoutes.configurationsUsers}`)
  }, [navigate])

  const handleNavigateToUnity = useCallback((sysId: string) => {
    navigate(`${ROUTES_NAMES.configurationsRoutes.configurationsUnities}/${sysId}`)
  }, [navigate])

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue((e.target as HTMLInputElement).value)
  }, [setFilterValue])

  return (
    <div className='bg-white flex-1 pt-[37px]'>
      <HeaderContainer>
        <CrxButtonIcon
          ariaLabel="Back Button Icon"
          icon="BackLine"
          onClick={handleNavigateBack}
          size="md"
        />
        <CrxHeading size="sm">Unidades</CrxHeading>
      </HeaderContainer>
      <FilterContainer>
        <CrxSearchContainer
          id="input-text"
          label="Busque por palavra chave..."
          placeholder="Busque por palavra chave..."
          value={filterValue}
          onChange={handleFilterChange}
        />
      </FilterContainer>
      <Container>
        {isLoading && locationSelector.locationsListPaginate.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            width: '100%',
            marginTop: 64,
          }}>
            <CrxLoading />
            <div style={{ color: '#666', fontSize: 14, marginTop: 16 }}>
              Carregando unidades...
            </div>
          </div>
        ) : locationSelector.locationsListPaginate.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            width: '100%',
            marginTop: 64, // 2x spacing-4x (32px)
          }}>
            <div style={{ fontWeight: 500, color: '#222', fontSize: 16 }}>
              Não encontramos resultados para sua busca.
            </div>
            <div style={{ color: '#666', fontSize: 14, marginTop: 8 }}>
              Tente revisar o termo digitado ou refazer a busca com outras palavras.
            </div>
          </div>
        ) : (
          <CrxTable>
            <CrxTableHeadRow>
              {tableHeader.map(i => (
                <CrxTableHeadLabel key={i.title} style={{ width: `${i.span * 100 / 12}%` }}>
                  <div className="flex items-center gap-5">
                    {i.title}
                    {i.sort && (
                      <div 
                        onClick={() => handleClickOrderBy(i.field)}
                        style={{ cursor: 'pointer' }}
                      >
                        <TbArrowsUpDown className="w-4 h-4 text-black" />
                      </div>
                    )}
                  </div>
                </CrxTableHeadLabel>
              ))}
            </CrxTableHeadRow>
            {locationSelector.locationsListPaginate.map(row => (
              <CrxTableDataRow
                key={row?.sys_id}
                style={{ cursor: 'pointer' }}
                onClick={() => handleNavigateToUnity(row?.sys_id)}
              >
                <CrxTableDataLabel>
                  {highlightText(row?.name ?? '', filterValue ?? '')}
                </CrxTableDataLabel>
                <CrxTableDataLabel>
                  {row?.active_sectors}
                </CrxTableDataLabel>
                <CrxTableDataLabel>
                  {row?.inactive_sectors}
                </CrxTableDataLabel>
                <CrxTableDataLabel>
                  <CrxIcon
                    icon="GoLine"
                    size="md"
                  />
                </CrxTableDataLabel>
              </CrxTableDataRow>
            ))}
          </CrxTable>
        )}
        
        {hasMoreData && (
          <div ref={ref} className="flex justify-center items-center py-4">
            {isLoading && <CrxLoading />}
          </div>
        )}
        
        {!hasMoreData && locationSelector.locationsListPaginate.length > 0 && (
          <div className="flex justify-center items-center py-4 text-gray-500">
            Todas as unidades foram carregadas
          </div>
        )}
      </Container>
    </div >
  )
}

export { Unities }